import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleQuestion } from '@fortawesome/sharp-solid-svg-icons';
import clsx from 'clsx';
import React, { FC, useState } from 'react';
import { Controller } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

import { GLOBAL_TOOLTIP } from '@/constants/constants';
import {
  EndAdornmentOptions,
  IFormFieldProps,
  InputSize,
  Status,
  statusIcons,
} from '@/models/form/form';
import { handleStatus } from '@/utils/form';

import PopperTooltipWrapper from '../popperTooltipWrapper';
import Typography from '../typography';
import FormBorderWrapper from './formBorderWrapper';
import FormCharacterCount from './formCharacterCount';

// This is designed so that any form field intended to be controlled can utilize this wrapper component for common UI features like character limits, form validation, helper text, and labels

export interface IControllerProps extends IFormFieldProps {
  FormField: FC<IFormFieldProps>;
  borderShouldShow?: boolean;
  formControllerClassName?: string;
}

const FormController = ({
  additionalBlur = undefined,
  additionalProps = {},
  className,
  control,
  dataTestId,
  description = '',
  disabled,
  endAdornment,
  FormField,
  helpText = '',
  helpTextPlace = 'right-end',
  isAutoCalculated,
  allowNegative = false,
  onInputChange,
  label,
  labelClassName = '',
  name = '',
  onChange,
  options,
  placeholder,
  rules,
  size = InputSize.MEDIUM,
  type,
  // props to hide parts of the controller design
  borderShouldShow = true,
  charCountShouldShow = true,
  showEndAdornment = true,
  showClearButton = true,
  showStartAdornment = true,
  hidePlaceholder = false,
  startAdornment,
  required,
  moreInfo,
  inputValue,
  async,
  loadOptions,
  defaultOptions,
  cacheOptions,
  showErrorMessage = true,
  hoverToolTip = undefined,
  formControllerClassName = undefined,
  autoFocus = false,
}: IControllerProps) => {
  const [focused, setFocused] = useState(false);
  const [touched, setTouched] = useState(false);

  return (
    <Controller
      control={control}
      name={name}
      render={({
        field: { value, onChange: rhfChange, onBlur },
        fieldState: { error },
      }) => {
        const errorMessage =
          error && !error.message
            ? Object.values(error as Object).map((e) => e.message)[0]
            : error?.message;
        const defaultEndAdornment = (
          endAdornmentOptions?: EndAdornmentOptions
        ) => {
          if (!showEndAdornment) return null;
          const key = handleStatus(focused, error, touched, value);
          return key !== Status.NONE && key !== Status.FOCUSED ? (
            <FontAwesomeIcon
              className={endAdornmentOptions?.className}
              icon={statusIcons[key].icon}
              size={endAdornmentOptions?.size}
              style={{ color: statusIcons[key].color }}
            />
          ) : null;
        };

        return (
          <div
            className={twMerge(
              'relative w-full opacity-100',
              formControllerClassName
            )}
          >
            {(label || helpText) && (
              <div className="mb-2 flex items-center justify-between">
                <div className="flex items-center gap-x-2">
                  <label
                    className={clsx('flex break-all', disabled && 'opacity-30')}
                    htmlFor={name}
                  >
                    <Typography
                      className={twMerge('text-gray-100', labelClassName)}
                      variant="button2"
                    >
                      {label}
                      {(rules?.required || required) && (
                        <span className="text-red-utility"> *</span>
                      )}
                      {moreInfo && (
                        <FontAwesomeIcon
                          className="ml-1 cursor-pointer text-green-50"
                          data-tooltip-content={moreInfo}
                          data-tooltip-delay-show={300}
                          data-tooltip-id={GLOBAL_TOOLTIP}
                          icon={faCircleQuestion}
                        />
                      )}
                    </Typography>
                  </label>

                  {helpText && (
                    <PopperTooltipWrapper
                      offset={[0, 24]}
                      placement={helpTextPlace}
                      popperBody={
                        <Typography className="text-white" variant="h6">
                          {helpText}
                        </Typography>
                      }
                      variant="hover"
                    >
                      <FontAwesomeIcon
                        className="text-gray-70"
                        icon={faCircleQuestion}
                        size="xs"
                      />
                    </PopperTooltipWrapper>
                  )}
                </div>
                {charCountShouldShow && (
                  <FormCharacterCount rules={rules} value={value} />
                )}
              </div>
            )}

            {description && (
              <div className="mb-2">
                <Typography className="text-gray-100" variant="body2">
                  {description}
                </Typography>
              </div>
            )}
            <FormBorderWrapper
              disabled={disabled && !isAutoCalculated}
              error={error}
              focused={focused}
              shouldShow={borderShouldShow}
              touched={touched}
              value={value}
            >
              <FormField
                additionalProps={additionalProps}
                allowNegative={allowNegative}
                async={async}
                autoFocus={autoFocus}
                cacheOptions={cacheOptions}
                className={className}
                dataTestId={dataTestId}
                defaultOptions={defaultOptions}
                disabled={disabled || isAutoCalculated}
                error={error}
                focused={focused}
                hidePlaceholder={hidePlaceholder}
                hoverToolTip={hoverToolTip}
                id={name}
                inputProps={{
                  max: rules?.max,
                  maxLength: rules?.maxLength,
                  min: rules?.min,
                  minLength: rules?.minLength,
                }}
                inputValue={inputValue}
                loadOptions={loadOptions}
                name={name}
                options={options}
                placeholder={placeholder}
                required={Boolean(rules?.required)}
                showClearButton={showClearButton}
                showEndAdornment={showEndAdornment}
                showStartAdornment={showStartAdornment}
                size={size}
                startAdornment={startAdornment}
                touched={touched}
                type={type}
                value={value}
                onBlur={() => {
                  setFocused(false);
                  additionalBlur?.();
                  onBlur();
                }}
                onChange={(e) => {
                  rhfChange(e);
                  onChange?.(e);
                }}
                onFocus={(f: boolean) => {
                  setFocused(f);
                  setTouched(true);
                }}
                // eslint-disable-next-line react/jsx-sort-props
                onInputChange={onInputChange}
                // However, autocalculated fields cannot be modified by the user directly
                endAdornment={endAdornment || defaultEndAdornment}
              />
            </FormBorderWrapper>
            {showErrorMessage && (
              <div className="mt-1 flex min-h-[22px] items-center justify-between break-all">
                <Typography
                  className="break-normal text-red-utility"
                  variant="caption"
                >
                  {errorMessage}
                </Typography>
              </div>
            )}
          </div>
        );
      }}
      rules={{ ...rules, required }}
    />
  );
};

export default FormController;
